import { sendEventToApp } from "@/utils/appCommunication";
import { PropsWithChildren, useState } from "react";
import { IoMdCloudDownload } from "react-icons/io";
import zod from "zod";
import PrimaryButton from "../Forms/PrimaryButton";

const semvarSchema = zod.string().regex(/^\d+\.\d+\.\d+$/);

const MIN_SUPPORTED_APP_VERSION = "1.0.1";

const isOutOfDate = () => {
    if (!window.isMobileApp) return false;
    if (!window.mobileAppVersion) return true;

    const currentVersion = window.mobileAppVersion;
    const currentVersionNums = semvarSchema
        .safeParse(currentVersion)
        .data?.split(".")
        ?.map(Number);
    if (!currentVersionNums) return true;
    const minVersionNums = MIN_SUPPORTED_APP_VERSION.split(".").map(Number);

    for (let i = 0; i < 3; i++) {
        const current = currentVersionNums[i] ?? 0;
        const min = minVersionNums[i] ?? 0;
        if (current < min) return true;
        if (current > min) return false;
    }

    return false;
};

export default function AppUpdateWarning({ children }: PropsWithChildren) {
    const [showWarning, setShowWarning] = useState(isOutOfDate());

    return showWarning ? (
        <div className="flex flex-col gap-4 h-[100dvh] items-center justify-center px-4">
            <div className="flex items-center justify-center bg-brand-red/20 size-25 rounded-3xl">
                <IoMdCloudDownload className="text-6xl text-brand-red" />
            </div>
            <div className="text-2xl font-bold text-brand-black text-center">
                A new version of the app is available
            </div>
            <div className="text-brand-gray -mt-2 text-center">
                Update to the latest version for the best experience. Older
                versions may not work as expected.
            </div>
            <div className="text-brand-gray -mt-2 mb-4 text-center">
                Already on the latest version? Please restart your app.
            </div>
            <PrimaryButton
                className="py-3 w-full sm:w-72 justify-center"
                onClick={() =>
                    sendEventToApp(
                        "openExternalLink",
                        "https://apps.apple.com/us/app/strawberry-me-coaching/id6751657658",
                    )
                }
            >
                Update
            </PrimaryButton>
            <button onClick={() => setShowWarning(false)}>Not now</button>
        </div>
    ) : (
        children
    );
}
